import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ConnectedAccountsChip from "../ConnectedAccountsChip/ConnectedAccountsChip";
import { formatIntegrationLabel } from "../utils/integrationUtils";
import { RegoAIConnectTooltipTitle } from "../RegoAIEvidenceConnectAlert";
import { MasterLookupItemsType } from "../../../../utils/enums";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../../../components/AccessControl";

const IntegrationCard = ({
  integration,
  hasHrmsConnected,
  hasIdpConnected,
  onManageIntegration,
  onConnectIntegration,
}) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );

  const isCloudProvider = (key) => {
    const cloudProviders = ["aws", "azure", "gcp"];
    return cloudProviders.includes(key?.toLowerCase());
  };
  const isHrmsIntegration =
    integration.domainKey === MasterLookupItemsType.HRMS_PROVIDER;
  const isIdpIntegration =
    integration.domainKey === MasterLookupItemsType.IDP_PROVIDER;
  const AI_EVIDENCE_DOMAINS = new Set([
    MasterLookupItemsType.EMAIL_PROVIDER,
    MasterLookupItemsType.FILE_STORAGE_PROVIDER,
    MasterLookupItemsType.KNOWLEDGE_BASE_PROVIDER,
    MasterLookupItemsType.TASK_MANAGEMENT_PROVIDER,
  ]);
  const showsAiEvidence =
    aiEvidenceEnabled &&
    (AI_EVIDENCE_DOMAINS.has(integration.domainKey) ||
      integration.key === "github" ||
      integration.key === "github_no_code_access");

  const renderActionButton = () => {
    // Check if this is AWS, Azure, or GCP
    if (isCloudProvider(integration.key)) {
      if (integration?.connectedAccounts?.length > 0) {
        // Show Manage button for AWS, Azure, GCP
        return (
          <Button
            variant="contained"
            onClick={() => onManageIntegration(integration)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.customColors.button.text,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              height: "38px",
              "&:hover": {
                backgroundColor: theme.customColors.button.hover,
              },
            }}
          >
            Manage
          </Button>
        );
      }
    } else {
      const hasAnyAccounts =
        (integration?.activeConnectedAccounts?.length || 0) +
          (integration?.inactiveConnectedAccounts?.length || 0) >
        0;
      if (hasAnyAccounts) {
        // Same as cloud: open management drawer (accounts, disconnect, system owners).
        // Inactive-only cards land here too so the user can reauth from the drawer.
        return (
          <Button
            variant="contained"
            onClick={() => onManageIntegration(integration)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.customColors.button.text,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              height: "38px",
              "&:hover": {
                backgroundColor: theme.customColors.button.hover,
              },
            }}
          >
            Manage
          </Button>
        );
      }
    }
    const shouldDisable =
      (isHrmsIntegration && hasHrmsConnected) ||
      (isIdpIntegration && hasIdpConnected);

    const button = (
      <Button
        variant="contained"
        onClick={() => onConnectIntegration(integration)}
        disabled={shouldDisable}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.customColors.button.text,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          px: 3,
          py: 1,
          height: "38px",
          "&:hover": {
            backgroundColor: theme.customColors.button.hover,
          },
          "&:disabled": {
            backgroundColor: theme.customColors.background.disabled,
            color: theme.customColors.text.disabled,
          },
        }}
      >
        Connect
      </Button>
    );

    if (shouldDisable) {
      const tooltipMessage = isHrmsIntegration
        ? "Only one HRMS integration can be connected at a time."
        : "Only one IDP integration can be connected at a time.";
      return (
        <Tooltip title={tooltipMessage} arrow>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Card
      sx={{
        width: "100%",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        borderRadius: "12px",
        transition: "all 0.3s ease",
        backgroundColor: "#FFFFFF",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          "&:last-child": { pb: 2.5 },
        }}
      >
        <Box>
          {/* Top section - Logo, Title, and Status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            {/* Left side - Logo and Title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "10px",
                  backgroundColor: theme.customColors.background.cardHeader,
                  border: `1px solid ${theme.customColors.border.main}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                {integration.logo_path ? (
                  <img
                    src={`${process.env.REACT_APP_POLICY_DOC_URL}/logos/${integration.logo_path}`}
                    alt={formatIntegrationLabel(integration.label)}
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 600,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    {formatIntegrationLabel(integration.label)?.charAt(0) ||
                      "?"}
                  </Typography>
                )}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: theme.customColors.text.strong,
                  }}
                >
                  {formatIntegrationLabel(integration.label)}
                </Typography>
              </Box>
            </Box>

            {/* Right side - Connect/Manage Status */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              {renderActionButton()}
              {showsAiEvidence && (
                <Tooltip
                  arrow
                  placement="top"
                  title={<RegoAIConnectTooltipTitle />}
                >
                  <Box
                    aria-label="Rego AI"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: -12,
                      zIndex: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      height: "18px",
                      px: 0.75,
                      borderRadius: "9px",
                      bgcolor: tc.background.main,
                      border: `1px solid ${alpha(tc.status.info.main, 0.35)}`,
                      boxShadow: tc.shadow.card,
                      cursor: "default",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: "11px",
                        color: tc.status.info.main,
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        color: tc.status.info.text,
                        lineHeight: 1,
                      }}
                    >
                      Rego AI
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Connected Accounts Section */}
          {integration.connectedAccounts.length > 0 && (
            <>
              <Divider
                sx={{
                  my: 2,
                  borderColor: theme.customColors.border.subtle,
                }}
              />
              <ConnectedAccountsChip
                connectedAccounts={integration.connectedAccounts}
                integrationKey={integration.key}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default IntegrationCard;
